import { yupResolver } from '@hookform/resolvers/yup';
import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import AlertModal from 'components/common/AlertModal';
import ConfirmModal from 'components/common/ConfirmModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import DateRangeControl from 'components/formfields/DateRangeControl';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { TIME_FRAME_PROPERTY, TIME_FRAME_SOURCE } from 'features/SaleType/constants';
import { SaleType } from 'features/SaleType/models';
import { TimeFrame } from 'features/SaleType/models/TimeFrame';
import saleTypeService from 'features/SaleType/saleTypeService';
import { checkTimeFrameIntersection, checkValidTimeRange } from 'features/SaleType/utils';
import { ChangeEvent } from 'react';
import { Button, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { displayError, handleServiceError } from 'utils';
import * as yup from 'yup';
import './style.scss';

export interface SaleTypeDetailModalProps {
  saleType?: SaleType;
  onClose: (isDataChanged: boolean) => void;
}

type FormValues = {
  id?: number;
  name: string;
  dates: {
    busy_season_start: string | null;
    busy_season_end: string | null;
  };
  timeFrames: TimeFrame[];
};

function SaleTypeDetailModal({ saleType, onClose }: SaleTypeDetailModalProps) {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  // yup setup
  const schema = yup.object({
    name: yup.string().required(t('SaleTypeDetail.warn_field_required')),
    dates: yup.object().shape({
      busy_season_start: yup.string().nullable(),
      // .transform((value, originalValue) => {
      //   return originalValue ? new Date(originalValue) : null;
      // })
      // .typeError(t('SaleTypeDetail.warn_invalid_date')),
      busy_season_end: yup
        .string()
        .nullable()
        // .transform((value, originalValue) => {
        //   return originalValue ? new Date(originalValue) : null;
        // })
        // .typeError(t('SaleTypeDetail.warn_invalid_date'))
        .when('busy_season_start', (busy_season_start) => {
          if (busy_season_start[0]) {
            return (
              yup
                .string()
                // .typeError(t('SaleTypeDetail.warn_input_end_date'))
                .required(t('SaleTypeDetail.warn_input_end_date'))
            );
            // .min(yup.ref('busy_season_start'), t('SaleTypeDetail.warn_min_end_date'));
          }
          return yup.date().nullable();
        }),
    }),
    timeFrames: yup.array(
      yup.object({
        name: yup.string().required(t('SaleTypeDetail.warn_field_required')),
        // start: yup.string().required(t('SaleTypeDetail.warn_field_required')),
        // end: yup.string().required(t('SaleTypeDetail.warn_field_required')),
        start: yup.string().nullable(),
        end: yup.string().nullable(),
        holiday_start: yup.string().nullable(),
        holiday_end: yup.string().nullable(),
        busy_time_start: yup.string().nullable(),
        busy_time_end: yup.string().nullable(),
        property: yup.number().required(t('SaleTypeDetail.warn_field_required')),
      })
    ),
  });

  let defaultFormValue: FormValues = {
    name: '',
    dates: {
      busy_season_start: null,
      busy_season_end: null,
    },
    timeFrames: [],
  };
  if (saleType) {
    if (saleType.id) {
      defaultFormValue = {
        id: saleType.id,
        name: saleType.name,
        dates: {
          busy_season_start: saleType.busy_season_start,
          busy_season_end: saleType.busy_season_end,
        },
        timeFrames: saleType.timeframes,
      };
    } else {
      defaultFormValue = {
        name: saleType.name,
        dates: {
          busy_season_start: saleType.busy_season_start,
          busy_season_end: saleType.busy_season_end,
        },
        timeFrames: saleType.timeframes,
      };
    }
  }

  // form setup
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: defaultFormValue,
    mode: 'onSubmit',
  });
  const { fields, append, remove, update } = useFieldArray({
    name: 'timeFrames',
    control,
    keyName: 'key',
  });

  function handleClose() {
    onClose?.(false);
  }

  async function processAddNewSaleType(data: SaleType) {
    try {
      const response = await saleTypeService.add(data);
      showGlobalModal(() => <AlertModal title={t('App.info')} message={response.message} />);
      onClose?.(true);
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }

  async function processUpdateSaleType(data: SaleType) {
    try {
      const response = await saleTypeService.update(data);
      showGlobalModal(() => (
        <AlertModal
          title={t('App.info')}
          message={response.message}
          onClose={() => {
            setTimeout(() => {
              showGlobalModal(() => (
                <AlertModal title={t('App.info')} message={t('SaleTypeDetail.check_menu_book_note')} />
              ));
            }, 0);
          }}
        />
      ));
      onClose?.(true);
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }

  function checkValidWeeklyTimeFramePayload(timeFrames: TimeFrame[]) {
    const isValidTimeRange = checkValidTimeRange(timeFrames, 'start', 'end');
    if (!isValidTimeRange) {
      showGlobalModal(() => <AlertModal title={t('App.info')} message={t('SaleTypeDetail.warn_timeframe_invalid')} />);
      return false;
    }

    const isTimeFrameIntersection = checkTimeFrameIntersection(timeFrames, 'start', 'end');
    if (isTimeFrameIntersection) {
      showGlobalModal(() => (
        <AlertModal title={t('App.info')} message={t('SaleTypeDetail.warn_timeframe_intersection')} />
      ));
      return false;
    }

    return true;
  }

  function checkValidHolidayTimeFramePayload(timeFrames: TimeFrame[]) {
    const isValidTimeRange = checkValidTimeRange(timeFrames, 'holiday_start', 'holiday_end');
    if (!isValidTimeRange) {
      showGlobalModal(() => (
        <AlertModal title={t('App.info')} message={t('SaleTypeDetail.warn_holiday_timeframe_invalid')} />
      ));
      return false;
    }

    const isTimeFrameIntersection = checkTimeFrameIntersection(timeFrames, 'holiday_start', 'holiday_end');
    if (isTimeFrameIntersection) {
      showGlobalModal(() => (
        <AlertModal title={t('App.info')} message={t('SaleTypeDetail.warn_holiday_timeframe_intersection')} />
      ));
      return false;
    }

    return true;
  }

  function checkValidBusyTimeFramePayload(timeFrames: TimeFrame[]) {
    const isValidTimeRange = checkValidTimeRange(timeFrames, 'busy_time_start', 'busy_time_end');
    if (!isValidTimeRange) {
      showGlobalModal(() => (
        <AlertModal title={t('App.info')} message={t('SaleTypeDetail.warn_busy_timeframe_invalid')} />
      ));
      return false;
    }

    const isTimeFrameIntersection = checkTimeFrameIntersection(timeFrames, 'busy_time_start', 'busy_time_end');
    if (isTimeFrameIntersection) {
      showGlobalModal(() => (
        <AlertModal title={t('App.info')} message={t('SaleTypeDetail.warn_busy_timeframe_intersection')} />
      ));
      return false;
    }

    return true;
  }

  function checkValidTimeFramePayload(timeFrames: TimeFrame[]) {
    const isValidWeeklyTimeFramePayload = checkValidWeeklyTimeFramePayload(timeFrames);
    const isValidHolidayTimeFramePayload = checkValidHolidayTimeFramePayload(timeFrames);
    const isValidBusyTimeFramePayload = checkValidBusyTimeFramePayload(timeFrames);

    return isValidWeeklyTimeFramePayload && isValidHolidayTimeFramePayload && isValidBusyTimeFramePayload;
  }

  function cleanData(data: FormValues) {
    data.dates.busy_season_start = data.dates.busy_season_start?.length === 0 ? null : data.dates.busy_season_start;
    data.dates.busy_season_end = data.dates.busy_season_end?.length === 0 ? null : data.dates.busy_season_end;
    data.timeFrames.forEach((timeframe) => {
      timeframe.start = timeframe.start?.length === 0 ? null : timeframe.start;
      timeframe.end = timeframe.end?.length === 0 ? null : timeframe.end;
      timeframe.holiday_start = timeframe.holiday_start?.length === 0 ? null : timeframe.holiday_start;
      timeframe.holiday_end = timeframe.holiday_end?.length === 0 ? null : timeframe.holiday_end;
      timeframe.busy_time_start = timeframe.busy_time_start?.length === 0 ? null : timeframe.busy_time_start;
      timeframe.busy_time_end = timeframe.busy_time_end?.length === 0 ? null : timeframe.busy_time_end;
    });
  }

  function onSubmit(data: FormValues) {
    cleanData(data);
    if (data.id) {
      // componse data
      const updatedTimeFrames = JSON.parse(JSON.stringify(saleType?.timeframes)) as TimeFrame[];
      updatedTimeFrames.forEach((timeframe) => {
        const foundDataTimeFrameItem = data.timeFrames.find((item) => item.id && item.id === timeframe.id);
        if (foundDataTimeFrameItem) {
          timeframe.name = foundDataTimeFrameItem.name;
          timeframe.start = foundDataTimeFrameItem.start;
          timeframe.end = foundDataTimeFrameItem.end;
          timeframe.holiday_start = foundDataTimeFrameItem.holiday_start;
          timeframe.holiday_end = foundDataTimeFrameItem.holiday_end;
          timeframe.busy_time_start = foundDataTimeFrameItem.busy_time_start;
          timeframe.busy_time_end = foundDataTimeFrameItem.busy_time_end;
          timeframe.property = Number(foundDataTimeFrameItem.property);
        } else {
          timeframe.delete_flg = true;
        }
      });
      const activeTimeFrames = data.timeFrames.filter(
        (item) => item.id === undefined || (item.id && item.delete_flg === false)
      );

      // compose timeFramesPayload
      if (checkValidTimeFramePayload(activeTimeFrames)) {
        const formData: SaleType = {
          id: data.id,
          name: data.name,
          busy_season_start: data.dates.busy_season_start,
          busy_season_end: data.dates.busy_season_end,
          timeframes: data.timeFrames,
        };
        processUpdateSaleType(formData);
      }
    } else {
      // insert/copy data
      if (checkValidTimeFramePayload(data.timeFrames)) {
        const formData: SaleType = {
          name: data.name,
          busy_season_start: data.dates.busy_season_start,
          busy_season_end: data.dates.busy_season_end,
          timeframes: data.timeFrames,
        };
        processAddNewSaleType(formData);
      }
    }
  }

  function handleAppendNewTimeFrame() {
    append({
      name: '',
      start: null,
      end: null,
      holiday_start: null,
      holiday_end: null,
      busy_time_start: null,
      busy_time_end: null,
      property: 0,
      delete_flg: false,
    });
  }

  function processRemoveTimeFrame() {
    const timeFrameCheckedElems = document.querySelectorAll('input[name=timeframe-selected]:checked');
    if (timeFrameCheckedElems) {
      const arrRemoveIndex: number[] = [];
      timeFrameCheckedElems.forEach(function (elem) {
        const index = Number((elem as HTMLInputElement).value);
        arrRemoveIndex.push(index);
      });

      const deletedIndexs: number[] = [];
      if (arrRemoveIndex.length > 0) {
        arrRemoveIndex.forEach((removeIndex) => {
          const removeItem = fields[removeIndex];
          if (removeItem.id) {
            removeItem.delete_flg = true;
            update(removeIndex, removeItem);
          } else {
            deletedIndexs.push(removeIndex);
          }
        });

        if (deletedIndexs.length > 0) {
          remove(deletedIndexs);
        }

        setTimeout(() => {
          showGlobalModal(() => <AlertModal title={t('App.info')} message={t('SaleTypeDetail.delete_successful')} />);
        }, 300);
      }
    }
  }

  function handleRemoveTimeFrame() {
    const timeFrameCheckedElems = document.querySelectorAll('input[name=timeframe-selected]:checked');
    if (timeFrameCheckedElems && timeFrameCheckedElems.length > 0) {
      showGlobalModal(() => (
        <ConfirmModal
          title={t('App.confirm')}
          message={t('SaleTypeDetail.warn_confirm_delete_timeframe')}
          onConfirm={processRemoveTimeFrame}
        />
      ));
    }
  }

  async function processRestore(index: number) {
    const restoreItem = fields[index];
    restoreItem.delete_flg = false;
    update(index, restoreItem);

    setTimeout(() => {
      showGlobalModal(() => <AlertModal title={t('App.info')} message={t('SaleTypeDetail.restore_successful')} />);
    }, 300);
  }

  function handleRestore(index: number) {
    showGlobalModal(() => (
      <ConfirmModal
        title={t('App.confirm')}
        message={t('SaleTypeListPage.warn_confirm_restore')}
        onConfirm={processRestore.bind(null, index)}
      />
    ));
  }

  function handleChangeTimeFrameWeekly(event: ChangeEvent<HTMLSelectElement>, index: number) {
    const selectedTimeFrame = fields[index];
    const value = event.target.value;
    if (value.length === 0) {
      selectedTimeFrame.start = null;
      selectedTimeFrame.end = null;
      update(index, selectedTimeFrame);
    }
  }

  function handleChangeTimeFrameHoliday(event: ChangeEvent<HTMLSelectElement>, index: number) {
    const selectedTimeFrame = fields[index];
    const value = event.target.value;
    if (value.length === 0) {
      selectedTimeFrame.holiday_start = null;
      selectedTimeFrame.holiday_end = null;
      update(index, selectedTimeFrame);
    }
  }

  function handleChangeTimeFrameBusy(event: ChangeEvent<HTMLSelectElement>, index: number) {
    const selectedTimeFrame = fields[index];
    const value = event.target.value;
    if (value.length === 0) {
      selectedTimeFrame.busy_time_start = null;
      selectedTimeFrame.busy_time_end = null;
      update(index, selectedTimeFrame);
    }
  }

  return (
    <Modal show backdrop="static" dialogClassName="saletype-detail-modal" size="lg" onHide={handleClose} centered>
      <Modal.Header closeButton className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        <FeatureTitleBar title={t('SaleTypeDetail.head_title')}></FeatureTitleBar>
      </Modal.Header>
      <Modal.Body className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <div className="mt-3">
            <Row>
              <Col sm={5}>
                <Table className="table-form mb-0" border={1}>
                  <tbody>
                    <tr>
                      <th className="text-center" style={{ width: '30%' }}>
                        <label className="col-form-label">{t('SaleTypeListPage.sale_type_name')}</label>
                      </th>
                      <td>
                        <Form.Control {...register('name')} />
                        {errors?.name && (
                          <span className="small text-danger d-inline-block mt-1">{errors?.name?.message}</span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th className="text-center" style={{ width: '30%' }}>
                        <label className="col-form-label">{t('SaleTypeDetail.timeframe_busy_time')}</label>
                      </th>
                      <td>
                        <DateRangeControl
                          name="dates"
                          control={control}
                          placeholder={t('SaleTypeDetail.undefined')}
                          startFieldName="busy_season_start"
                          endFieldName="busy_season_end"
                        />
                        {errors?.dates?.busy_season_start && (
                          <span className="small text-danger d-inline-block mt-1">
                            {errors?.dates?.busy_season_start?.message}
                          </span>
                        )}
                        {errors?.dates?.busy_season_end && (
                          <span className="small text-danger d-inline-block mt-1">
                            {errors?.dates?.busy_season_end?.message}
                          </span>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </Col>
              <Col sm={7} className="text-end">
                <Button type="submit" className="btn--dark-green">
                  {t('SaleTypeDetail.save')}
                </Button>
              </Col>
            </Row>
          </div>
          <div className="d-flex justify-content-between align-items-start mt-3">
            <div>
              <h4 className="fw-bold mb-2">{t('SaleTypeDetail.register_timeframe')}</h4>
              <div>{t('SaleTypeDetail.timeframe_guide')}</div>
            </div>
            <Button className="btn--red" onClick={handleRemoveTimeFrame}>
              {t('SaleTypeListPage.delete')}
            </Button>
          </div>
          <div className="sticky-table flex-grow-1 flex-shrink-1 mt-2" style={{ maxHeight: '311px' }}>
            <Table hover>
              <thead>
                <tr>
                  <th rowSpan={3} style={{ width: '4%' }}>
                    {t('SaleTypeListPage.selection')}
                  </th>
                  <th rowSpan={3} style={{ width: '16%' }}>
                    {t('SaleTypeDetail.timeframe_name')}
                  </th>
                  <th colSpan={6} style={{ width: '60%' }}>
                    {t('SaleTypeDetail.apply_time')}
                  </th>
                  <th rowSpan={3} style={{ width: '20%' }}>
                    {t('SaleTypeDetail.property')}
                    <div dangerouslySetInnerHTML={{ __html: t('SaleTypeDetail.property_note') }}></div>
                  </th>
                </tr>
                <tr>
                  <th colSpan={2} style={{ width: '20%' }}>
                    {t('SaleTypeDetail.timeframe_time')}
                  </th>
                  <th colSpan={2} style={{ width: '20%' }}>
                    {t('SaleTypeDetail.timeframe_holiday_time')}
                  </th>
                  <th colSpan={2} style={{ width: '20%' }}>
                    {t('SaleTypeDetail.timeframe_busy_time')}
                  </th>
                </tr>
                <tr>
                  <th style={{ width: '10%' }}>{t('SaleTypeDetail.begin')}</th>
                  <th style={{ width: '10%' }}>{t('SaleTypeDetail.end')}</th>
                  <th style={{ width: '10%' }}>{t('SaleTypeDetail.begin')}</th>
                  <th style={{ width: '10%' }}>{t('SaleTypeDetail.end')}</th>
                  <th style={{ width: '10%' }}>{t('SaleTypeDetail.begin')}</th>
                  <th style={{ width: '10%' }}>{t('SaleTypeDetail.end')}</th>
                </tr>
              </thead>
              <tbody>
                {fields.map((field, index) => {
                  return (
                    <tr key={field.key}>
                      <td className="text-center">
                        {!field.delete_flg && (
                          <Form.Check className="custom-checkbox" name="timeframe-selected" value={index}></Form.Check>
                        )}
                        {field.delete_flg && (
                          <Button className="btn--orange" onClick={handleRestore.bind(null, index)}>
                            {t('SaleTypeListPage.restore')}
                          </Button>
                        )}
                      </td>
                      <td>
                        <Form.Control
                          className="text-center"
                          {...register(`timeFrames.${index}.name`)}
                          maxLength={255}
                          defaultValue={field.name}
                          disabled={field.delete_flg}
                        ></Form.Control>
                        {errors?.timeFrames?.[index]?.name && (
                          <span className="small text-danger d-inline-block mt-1">
                            {errors?.timeFrames?.[index]?.name?.message}
                          </span>
                        )}
                      </td>
                      <td>
                        <Form.Select
                          className="text-center"
                          {...register(`timeFrames.${index}.start`)}
                          disabled={field.delete_flg}
                          onChange={(event) => handleChangeTimeFrameWeekly(event, index)}
                        >
                          <option value="">{t('SaleTypeDetail.undefined')}</option>
                          {Object.keys(TIME_FRAME_SOURCE).map(function (timeFrame) {
                            return (
                              <option key={timeFrame} value={timeFrame}>
                                {TIME_FRAME_SOURCE[timeFrame]}
                              </option>
                            );
                          })}
                        </Form.Select>
                        {errors?.timeFrames?.[index]?.start && (
                          <span className="small text-danger d-inline-block mt-1">
                            {errors?.timeFrames?.[index]?.start?.message}
                          </span>
                        )}
                      </td>
                      <td>
                        <Form.Select
                          className="text-center"
                          {...register(`timeFrames.${index}.end`)}
                          disabled={field.delete_flg}
                          onChange={(event) => handleChangeTimeFrameWeekly(event, index)}
                        >
                          <option value="">{t('SaleTypeDetail.undefined')}</option>
                          {Object.keys(TIME_FRAME_SOURCE).map(function (timeFrame) {
                            return (
                              <option key={timeFrame} value={timeFrame}>
                                {TIME_FRAME_SOURCE[timeFrame]}
                              </option>
                            );
                          })}
                        </Form.Select>
                        {errors?.timeFrames?.[index]?.end && (
                          <span className="small text-danger d-inline-block mt-1">
                            {errors?.timeFrames?.[index]?.end?.message}
                          </span>
                        )}
                      </td>
                      <td>
                        <Form.Select
                          className="text-center"
                          {...register(`timeFrames.${index}.holiday_start`)}
                          disabled={field.delete_flg}
                          onChange={(event) => handleChangeTimeFrameHoliday(event, index)}
                        >
                          <option value="">{t('SaleTypeDetail.undefined')}</option>
                          {Object.keys(TIME_FRAME_SOURCE).map(function (timeFrame) {
                            return (
                              <option key={timeFrame} value={timeFrame}>
                                {TIME_FRAME_SOURCE[timeFrame]}
                              </option>
                            );
                          })}
                        </Form.Select>
                        {errors?.timeFrames?.[index]?.holiday_start && (
                          <span className="small text-danger d-inline-block mt-1">
                            {errors?.timeFrames?.[index]?.holiday_start?.message}
                          </span>
                        )}
                      </td>
                      <td>
                        <Form.Select
                          className="text-center"
                          {...register(`timeFrames.${index}.holiday_end`)}
                          disabled={field.delete_flg}
                          onChange={(event) => handleChangeTimeFrameHoliday(event, index)}
                        >
                          <option value="">{t('SaleTypeDetail.undefined')}</option>
                          {Object.keys(TIME_FRAME_SOURCE).map(function (timeFrame) {
                            return (
                              <option key={timeFrame} value={timeFrame}>
                                {TIME_FRAME_SOURCE[timeFrame]}
                              </option>
                            );
                          })}
                        </Form.Select>
                        {errors?.timeFrames?.[index]?.holiday_end && (
                          <span className="small text-danger d-inline-block mt-1">
                            {errors?.timeFrames?.[index]?.holiday_end?.message}
                          </span>
                        )}
                      </td>
                      <td>
                        <Form.Select
                          className="text-center"
                          {...register(`timeFrames.${index}.busy_time_start`)}
                          disabled={field.delete_flg}
                          onChange={(event) => handleChangeTimeFrameBusy(event, index)}
                        >
                          <option value="">{t('SaleTypeDetail.undefined')}</option>
                          {Object.keys(TIME_FRAME_SOURCE).map(function (timeFrame) {
                            return (
                              <option key={timeFrame} value={timeFrame}>
                                {TIME_FRAME_SOURCE[timeFrame]}
                              </option>
                            );
                          })}
                        </Form.Select>
                        {errors?.timeFrames?.[index]?.busy_time_start && (
                          <span className="small text-danger d-inline-block mt-1">
                            {errors?.timeFrames?.[index]?.busy_time_start?.message}
                          </span>
                        )}
                      </td>
                      <td>
                        <Form.Select
                          className="text-center"
                          {...register(`timeFrames.${index}.busy_time_end`)}
                          disabled={field.delete_flg}
                          onChange={(event) => handleChangeTimeFrameBusy(event, index)}
                        >
                          <option value="">{t('SaleTypeDetail.undefined')}</option>
                          {Object.keys(TIME_FRAME_SOURCE).map(function (timeFrame) {
                            return (
                              <option key={timeFrame} value={timeFrame}>
                                {TIME_FRAME_SOURCE[timeFrame]}
                              </option>
                            );
                          })}
                        </Form.Select>
                        {errors?.timeFrames?.[index]?.busy_time_end && (
                          <span className="small text-danger d-inline-block mt-1">
                            {errors?.timeFrames?.[index]?.busy_time_end?.message}
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="d-flex flex-column justify-content-center align-items-center">
                          <div style={{ width: '150px' }}>
                            <Form.Select
                              className="text-center"
                              {...register(`timeFrames.${index}.property`)}
                              disabled={field.delete_flg}
                            >
                              {Object.keys(TIME_FRAME_PROPERTY).map(function (timeFrame) {
                                return (
                                  <option key={timeFrame} value={timeFrame}>
                                    {TIME_FRAME_PROPERTY[timeFrame]}
                                  </option>
                                );
                              })}
                            </Form.Select>
                            {errors?.timeFrames?.[index]?.property && (
                              <span className="small text-danger d-inline-block mt-1">
                                {errors?.timeFrames?.[index]?.property?.message}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </Form>
        <div className="text-center mt-3">
          <Button onClick={handleAppendNewTimeFrame}>{t('SaleTypeDetail.add_row')}</Button>
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default SaleTypeDetailModal;
